import React from 'react';
import { Plus, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';
import { PlanHeaderCard } from '@/components/production/PlanHeaderCard';
import { EventRow } from '@/components/production/EventRow';
import type { PlanMalElement } from '../hooks/usePlanMalDialog';
import { isPlanRadSortable } from '@/lib/planDndUtils';

interface PlanMalListProps {
  overskrifter: PlanMalElement[];
  elements: PlanMalElement[];
  expandedHeaders: Set<number>;
  activeElement: PlanMalElement | null;
  sensors: any;
  onToggleHeader: (id: number) => void;
  onEditOverskrift: (element: PlanMalElement) => void;
  onDeleteOverskrift: (id: number) => void;
  onEditHendelse: (element: PlanMalElement) => void;
  onDeleteHendelse: (id: number) => void;
  onAddHendelse: (parentId: number) => void;
  onAddMusikkPlaceholder: (parentId: number) => void;
  onDragStart: (event: any) => void;
  onDragOver: (event: any) => void;
  onDragEnd: (event: any) => void;
  onDragCancel: () => void;
}

export const PlanMalList: React.FC<PlanMalListProps> = ({
  overskrifter,
  elements,
  expandedHeaders,
  activeElement,
  sensors,
  onToggleHeader,
  onEditOverskrift,
  onDeleteOverskrift,
  onEditHendelse,
  onDeleteHendelse,
  onAddHendelse,
  onAddMusikkPlaceholder,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragCancel,
}) => {
  const getHendelserForOverskrift = (overskriftId: number): PlanMalElement[] => {
    return elements
      .filter(el => el.parent_id === overskriftId && (el.type === 'hendelse' || el.type === 'musikk_placeholder'))
      .sort((a, b) => a.rekkefølge - b.rekkefølge);
  };

  if (overskrifter.length === 0) {
    return (
      <div className="bg-muted rounded-xl p-8 sm:p-12 text-center border-2 border-dashed">
        <p className="text-sm sm:text-base text-muted-foreground">
          Ingen innslag ennå. Legg til en overskrift for å komme i gang.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext
        items={[...overskrifter.map(o => o.id), ...elements.filter(isPlanRadSortable).map(h => h.id)]}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2 sm:space-y-3">
          {overskrifter.map((overskrift) => {
            const hendelser = getHendelserForOverskrift(overskrift.id);
            const isExpanded = expandedHeaders.has(overskrift.id);
            const showDropZone =
              activeElement != null &&
              isPlanRadSortable(activeElement) &&
              activeElement.parent_id !== overskrift.id;

            return (
              <div key={overskrift.id} className="bg-card rounded-lg shadow-md border overflow-hidden">
                <PlanHeaderCard
                  id={overskrift.id}
                  name={overskrift.navn}
                  eventCount={hendelser.length}
                  førProduksjonsstart={overskrift.før_produksjonsstart}
                  isExpanded={isExpanded}
                  onToggle={() => onToggleHeader(overskrift.id)}
                  onEdit={(e) => {
                    e.stopPropagation();
                    onEditOverskrift(overskrift);
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    onDeleteOverskrift(overskrift.id);
                  }}
                  activeElementId={activeElement?.id}
                  activeElementType={activeElement?.type}
                />

                {isExpanded && (
                  <div className={cn(
                    "bg-muted/30 border-l-4 border-primary transition-all",
                    showDropZone ? 'bg-green-100/50 border-green-400' : ''
                  )}>
                    {showDropZone && (
                      <div className="p-3 bg-green-100 border-y border-green-300 text-green-800 text-sm font-medium flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        Slipp her for å flytte til "{overskrift.navn}"
                      </div>
                    )}
                    
                    {hendelser.length === 0 ? (
                      <div className={cn(
                        "p-4 text-sm text-muted-foreground italic",
                        showDropZone ? 'bg-green-50 text-green-700' : ''
                      )}>
                        {showDropZone ? 'Slipp her for å legge til som første innslag' : 'Ingen innslag ennå'}
                      </div>
                    ) : (
                      <SortableContext
                        items={hendelser.map(h => h.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {hendelser.map((hendelse) => (
                          <EventRow
                            key={hendelse.id}
                            event={{
                              ...hendelse,
                              varighet_minutter: hendelse.varighet_minutter ?? undefined,
                            }}
                            onEdit={() => onEditHendelse(hendelse)}
                            onDelete={() => onDeleteHendelse(hendelse.id)}
                            showDescription={false}
                            activeElementId={activeElement?.id}
                          />
                        ))}
                      </SortableContext>
                    )}
                    
                    <div className="p-2 sm:p-3 border-t border-border/50">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => onAddHendelse(overskrift.id)}
                          variant="outline"
                          size="sm"
                          className="gap-2 text-xs sm:text-sm flex-1"
                        >
                          <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                          Legg til innslag
                        </Button>
                        <Button
                          onClick={() => onAddMusikkPlaceholder(overskrift.id)}
                          variant="outline"
                          size="sm"
                          className="gap-2 text-xs sm:text-sm flex-1 border-purple-200 text-purple-700 hover:bg-purple-50"
                        >
                          <Music className="h-3 w-3 sm:h-4 sm:w-4" />
                          Musikk-placeholder
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SortableContext>
      
      <DragOverlay dropAnimation={null}>
        {activeElement && (
          <div className={cn(
            "bg-card border-2 border-primary shadow-2xl rounded-lg overflow-hidden",
            activeElement.type === 'overskrift' ? 'min-w-[300px]' : 'min-w-[250px]'
          )}>
            {activeElement.type === 'overskrift' ? (
              <div className="bg-primary text-primary-foreground p-3 flex items-center gap-2">
                <span className="font-semibold">{activeElement.navn}</span>
                <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground ml-auto">
                  {elements.filter(el => el.parent_id === activeElement.id).length}
                </Badge>
              </div>
            ) : (
              <div className="p-3 flex items-center gap-2">
                {activeElement.varighet_minutter && (
                  <Badge variant="outline" className="gap-1 bg-primary/10 text-primary border-primary/30 font-mono shrink-0">
                    <Clock className="h-3 w-3" />
                    {activeElement.varighet_minutter} min
                  </Badge>
                )}
                <span className="font-semibold flex-1">{activeElement.navn}</span>
              </div>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

